import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import WhatsAppFloating from '@/components/feature/WhatsAppFloating';

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}

function RevealSection({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

interface PublicPageLayoutProps {
  children: React.ReactNode;
  title: string;
  description: string;
  schemaType?: string;
  schemaJsonLd?: Record<string, unknown>;
}

export default function PublicPageLayout({ children, title, description, schemaType, schemaJsonLd }: PublicPageLayoutProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.title = title;
  }, [title]);

  return (
    <div className="min-h-screen bg-background-50 overflow-x-hidden">
      {/* Schema.org JSON-LD */}
      {schemaJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schemaJsonLd),
          }}
        />
      )}

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-background-100/80 backdrop-blur-md border-b border-background-200/60 dark:border-background-300/30">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[68px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
              <i className="ri-whatsapp-fill text-white text-lg" />
            </div>
            <span className="font-heading font-bold text-foreground-950 dark:text-foreground-100 text-[17px] tracking-tight">WZap PRO</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm text-foreground-600 dark:text-foreground-400 hover:text-foreground-900 dark:hover:text-foreground-200 transition-colors whitespace-nowrap">Início</Link>
            <Link to="/privacy" className="text-sm text-foreground-600 dark:text-foreground-400 hover:text-foreground-900 dark:hover:text-foreground-200 transition-colors whitespace-nowrap">Privacidade</Link>
            <Link to="/terms" className="text-sm text-foreground-600 dark:text-foreground-400 hover:text-foreground-900 dark:hover:text-foreground-200 transition-colors whitespace-nowrap">Termos</Link>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm font-medium text-foreground-700 dark:text-foreground-300 hover:text-foreground-950 dark:hover:text-foreground-100 transition-colors whitespace-nowrap"
            >
              Entrar
            </Link>
            <Link
              to="/login"
              className="text-sm font-medium bg-primary-500 hover:bg-primary-600 text-white px-5 py-2.5 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.98] whitespace-nowrap"
            >
              Testar Grátis
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-white/95 dark:bg-background-100/95 backdrop-blur-md border-b border-background-200/60 px-5 py-4 space-y-3">
            <Link to="/" className="block text-sm text-foreground-700 hover:text-primary-600 transition-colors" onClick={() => setMenuOpen(false)}>Início</Link>
            <Link to="/privacy" className="block text-sm text-foreground-700 hover:text-primary-600 transition-colors" onClick={() => setMenuOpen(false)}>Política de Privacidade</Link>
            <Link to="/terms" className="block text-sm text-foreground-700 hover:text-primary-600 transition-colors" onClick={() => setMenuOpen(false)}>Termos de Uso</Link>
          </div>
        )}
      </nav>

      {/* Hero Header for legal pages */}
      <section className="relative pt-[68px] min-h-[240px] md:min-h-[280px] flex items-center bg-gradient-to-br from-background-50 via-background-100 to-background-200 dark:from-[#08080d] dark:via-background-100 dark:to-background-200">
        <div className="relative z-10 w-full max-w-4xl mx-auto px-5 md:px-8 py-12 md:py-16">
          <RevealSection>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 dark:bg-primary-500/10 border border-primary-200 dark:border-primary-500/20 mb-5">
              <i className="ri-shield-check-line text-primary-600 text-sm" />
              <span className="text-xs font-semibold text-primary-700 dark:text-primary-400">Documento Oficial</span>
            </div>
          </RevealSection>
          <RevealSection delay={100}>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 dark:text-foreground-100 leading-tight tracking-tight">
              {title.replace(' | WZap PRO', '')}
            </h1>
          </RevealSection>
          <RevealSection delay={200}>
            <p className="text-base md:text-lg text-foreground-500 dark:text-foreground-400 max-w-2xl mt-4 leading-relaxed">
              {description}
            </p>
          </RevealSection>
        </div>
      </section>

      {/* Content */}
      <main className="py-10 md:py-16 px-5 md:px-8">
        <div className="max-w-3xl mx-auto">
          {children}
        </div>
      </main>

      {/* WhatsApp Floating */}
      <WhatsAppFloating />

      {/* Footer */}
      <footer className="bg-background-100 dark:bg-background-100 border-t border-background-200 dark:border-background-300 py-12 px-5 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                <i className="ri-whatsapp-fill text-white text-base" />
              </div>
              <span className="font-heading font-bold text-foreground-950 dark:text-foreground-100 text-base">WZap PRO</span>
            </Link>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-foreground-500 dark:text-foreground-400">
              <Link to="/#features" className="hover:text-foreground-800 dark:hover:text-foreground-200 transition-colors whitespace-nowrap">Recursos</Link>
              <Link to="/#how" className="hover:text-foreground-800 dark:hover:text-foreground-200 transition-colors whitespace-nowrap">Como Funciona</Link>
              <Link to="/#testimonials" className="hover:text-foreground-800 dark:hover:text-foreground-200 transition-colors whitespace-nowrap">Depoimentos</Link>
              <Link to="/#plans" className="hover:text-foreground-800 dark:hover:text-foreground-200 transition-colors whitespace-nowrap">Planos</Link>
              <Link to="/login" className="hover:text-foreground-800 dark:hover:text-foreground-200 transition-colors whitespace-nowrap">Entrar</Link>
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-xs text-foreground-400">
              <Link to="/privacy" className="hover:text-foreground-600 transition-colors">Privacidade</Link>
              <span className="text-foreground-300">•</span>
              <Link to="/terms" className="hover:text-foreground-600 transition-colors">Termos</Link>
              <span className="text-foreground-300">•</span>
              <Link to="/faq" className="hover:text-foreground-600 transition-colors">FAQ</Link>
              <span className="text-foreground-300">•</span>
              <Link to="/about" className="hover:text-foreground-600 transition-colors">Sobre</Link>
              <span className="text-foreground-300">•</span>
              <Link to="/contact" className="hover:text-foreground-600 transition-colors">Contato</Link>
            </div>
            <p className="text-xs text-foreground-400 text-center">
              &copy; {new Date().getFullYear()} WZap PRO. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}